<template>
  <div class="timeTrackingTable">
    <DataTable
      class="p-datatable-sm"
      :value="timeTrackings"
      :loading="isLoading"
      :rowClass="rowClass"
      :rowHover="true"
      dataKey="number"
      :lazy="true"
      :filters.sync="tableState.filters"
      stateStorage="local"
      :stateKey="filterName"
      filterDisplay="row"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      :totalRecords="getTimeTrackingCount"
      :paginator="true"
      :rows.sync="tableState.pagination.rowsPerPage"
      :first="pageOffset"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="'Anzeigen ' + '{first} bis {last} von {totalRecords} Einträgen'"
      :rowsPerPageOptions="[10, 20, 50, 100, 200]"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <div class="mt-2">
              <span
                v-if="
                  tableState.filters.startTime &&
                    tableState.filters.stopTime &&
                    getTimeTrackingCount &&
                    getTimeTrackingTotalHours
                "
              >
                Vom {{ $date(tableState.filters.startTime.value) }} bis zum
                {{ $date(tableState.filters.stopTime.value) }} wurden bei
                {{ getTimeTrackingCount }} Zeiterfassungen insgesamt
                {{ formatDuration(getTimeTrackingTotalHours) }} Arbeitsstunden geleistet.
              </span>
              <span v-else>
                Zeitraum oder Daten nicht vollständig definiert.
              </span>
            </div>
          </div>
          <div class="table-header-right">
            <button class="btn btn-narrow btn-inverse ml-2 mb-2" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <Button class="btn btn-narrow btn-inverse mr-2" @click="showNewTimeTrackingDialog">
              <i class="fa fa-plus mr-2"></i>
              {{ $t('new') }}
            </Button>
            <ButtonWait
              :startCallback="startTimeTrackingExportBuild"
              :pollCallback="fetchExportBuildState"
              class="pull-right"
            >
              <template v-slot:buttonText>
                {{ $t('generate_time_tracking_list') }}
              </template>
            </ButtonWait>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="startTimeDay"
        :header="$t('day')"
        sortable
        :sortField="'startTime'"
        :headerStyle="{ width: '180px' }"
        :showFilterMenu="false"
        filterField="dateRange"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.startTime">
            {{ formatDateWithDots(slotProps.data.startTime) }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            @input="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="startTime"
        :header="$t('start')"
        :styles="{ width: '100px' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <strong> {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong>
        </template>
      </Column>

      <Column
        field="stopTime"
        :header="$t('end')"
        :styles="{ width: '100px' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <strong v-if="slotProps.data[slotProps.column.field]">
            {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong
          >
          <strong v-else>
            <LoopingRhombusesSpinner
              :animation-duration="12000"
              :rhombus-size="9"
              color="#495057"
            />
          </strong>
        </template>
      </Column>

      <Column field="duration" :header="$t('duration')" sortable :headerStyle="{ width: '100px' }">
        <template #body="slotProps">
          <span>
            {{ formatDuration(slotProps.data.duration || 0) }}
          </span>
        </template>
      </Column>

      <Column
        field="timeTrackingState"
        :header="$t('state')"
        :styles="{ width: '7%' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge :variant="getTimeTrackingStateColor(slotProps.data.timeTrackingState)">
            {{ $t(slotProps.data.timeTrackingState) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableState.customFilters.timeTrackingStates"
            :options="timeTrackingStates"
            @input="onTimeTrackingStateFilter"
            optionLabel="label"
            :placeholder="$t('state')"
          >
          </MultiSelect>
        </template>
      </Column>

      <Column field="user" :header="$t('employee')" sortable :showFilterMenu="false">
        <template #body="slotProps">
          <div>
            {{ slotProps.data.user.firstname }}
            {{ slotProps.data.user.lastname }}
          </div>
        </template>
        <template #filter>
          <InputText
            :value="tableState.customFilters.timeTrackingUserSearch.value"
            @input="onUserFilter"
          />
        </template>
      </Column>

      <Column field="project" :header="$t('customer')" sortable :showFilterMenu="false">
        <template #body="slotProps">
          <router-link
            class="mr-2"
            style="display: inline-block"
            v-if="slotProps.data && slotProps.data.project && slotProps.data.project.number"
            target="_blank"
            :to="{
              name: 'ProjectEditPage',
              params: { projectNumber: slotProps.data.project.number },
            }"
            >{{ slotProps.data.project.number.toString().padStart(4, '0') }}
            <i class="fa fa-sm fa-external-link"></i>
          </router-link>
          <div
            style="display: inline-block"
            v-if="slotProps.data && slotProps.data.project && slotProps.data.project.customer"
          >
            {{ slotProps.data.project.customer.firstname || '' }}
            {{ slotProps.data.project.customer.lastname || '' }}
            <template v-if="slotProps.data.project.customer.street">
              , {{ slotProps.data.project.customer.street }}
            </template>
            <template v-if="slotProps.data.project.customer.streetNumber">
              , {{ slotProps.data.project.customer.streetNumber }}
            </template>
            <template v-if="slotProps.data.project.customer.streetNumberSuffix">
              , {{ slotProps.data.project.customer.streetNumberSuffix }}
            </template>
            <template v-if="slotProps.data.project.customer.zip">
              , {{ slotProps.data.project.customer.zip }}
            </template>
            <template v-if="slotProps.data.project.customer.city">
              {{ slotProps.data.project.customer.city }}
            </template>
          </div>
          <div v-else></div>
        </template>
        <template #filter>
          <InputText
            :value="tableState.customFilters.timeTrackingCustomerSearch.value"
            @input="onCustomerFilter"
          />
        </template>
      </Column>

      <Column :styles="{ 'min-width': '80px' }">
        <template #body="slotProps">
          <button class="btn btn-icon" @click="showEditDialog(slotProps.data)">
            <i class="link-icon fa fa-lg fa-edit mr-2"></i>
          </button>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :header="$t('time_tracking') + ' ' + (isNewTimeTracking ? $t('new') : $t('edit'))"
      :visible.sync="displayEditDialog"
      :contentStyle="{ overflow: 'visible' }"
      :modal="true"
    >
      <div v-if="editedTimeTracking">
        <div class="user-info">
          <div class="user-name" v-if="!isNewTimeTracking">
            {{ editedTimeTracking.user.firstname }} {{ editedTimeTracking.user.lastname }}
          </div>
          <div class="info-row">
            <div class="date-info">
              {{ $dayjs(editedTimeTracking.startTime).format('DD.MM.YYYY') }}<br />
              <template v-if="editedTimeTracking.stopTime">
                <span
                  v-if="
                    $dayjs(editedTimeTracking.startTime).format('DD.MM.YYYY') ===
                      $dayjs(editedTimeTracking.stopTime).format('DD.MM.YYYY')
                  "
                >
                  {{ $dayjs(editedTimeTracking.startTime).format('HH:mm') }} -
                  {{ $dayjs(editedTimeTracking.stopTime).format('HH:mm') }}
                </span>
                <span v-else>
                  {{ $dayjs(editedTimeTracking.startTime).format('DD.MM.YYYY HH:mm') }} -
                  {{ $dayjs(editedTimeTracking.stopTime).format('DD.MM.YYYY HH:mm') }}
                </span>
                <br />
                {{
                  formatDuration(
                    getDurationInMinutes(editedTimeTracking.startTime, editedTimeTracking.stopTime)
                  )
                }}
                Stunden
              </template>
              <template v-else-if="editedTimeTracking.timeTrackingState === 'STARTED'">
                {{ $dayjs(editedTimeTracking.startTime).format('HH:mm') }} -
                <span>{{ $t('running') }}...</span>
                <br />
                {{ formatDuration(getDurationInMinutes(editedTimeTracking.startTime, new Date())) }}
                Stunden
              </template>
            </div>
            <div class="state-badge">
              <b-badge :variant="getTimeTrackingStateColor(editedTimeTracking.timeTrackingState)">
                {{ $t(editedTimeTracking.timeTrackingState) }}
              </b-badge>
            </div>
          </div>
        </div>

        <div class="time-inputs" v-if="editedTimeTracking.timeTrackingState !== 'STARTED'">
          <div class="user-input" v-if="isNewTimeTracking">
            <label>{{ $t('employee') }}</label>
            <multiselect
              class="multiselect-users"
              v-model="editedTimeTracking.user"
              :options="filteredUsers"
              track-by="number"
              v-on:search-change="onFilterUsers"
              :filter="true"
              :internal-search="false"
              :multiple="false"
              :show-labels="false"
              :allow-empty="false"
              :deselectLabel="$t('deselectLabel')"
              selectLabel=""
              selectedLabel=""
              placeholder="Mitarbeiter"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ option.firstname }} {{ option.lastname }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ option.firstname }} {{ option.lastname }}
              </template>
              <template slot="tag" slot-scope="{ option }">
                <div>{{ option.firstname }} {{ option.lastname }}</div>
              </template>
              <template slot="noResult">
                <div>{{ $t('no_results') }}</div>
              </template>
            </multiselect>
          </div>
          <div class="time-row">
            <div class="time-input">
              <label>{{ $t('start') }}</label>
              <div class="time-input-with-calendar">
                <Calendar
                  class="mb-2"
                  v-model="editedTimeTracking.startTime"
                  :showTime="false"
                  :showOtherMonths="true"
                  :modal="true"
                  :appendTo="'body'"
                  @date-select="onStartDateSelect"
                />
              </div>
              <Calendar
                v-model="editedTimeTracking.startTime"
                :timeOnly="true"
                :showTime="true"
                :stepMinute="15"
                :manualInput="true"
                hourFormat="24"
                @time-select="onStartTimeSelect"
              />
            </div>
            <div class="time-input">
              <label>{{ $t('end') }}</label>
              <div class="time-input-with-calendar">
                <Calendar
                  class="mb-2"
                  v-model="editedTimeTracking.stopTime"
                  :showTime="false"
                  :showOtherMonths="true"
                  :modal="true"
                  :appendTo="'body'"
                  @date-select="onStopDateSelect"
                />
              </div>
              <Calendar
                v-model="editedTimeTracking.stopTime"
                :timeOnly="true"
                :showTime="true"
                :stepMinute="15"
                :manualInput="true"
                hourFormat="24"
                @time-select="onStopTimeSelect"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button class="btn btn-narrow btn-default ml-2 mb-2" @click="onCancelEdit">
          {{ $t('cancel') }}
        </Button>
        <template v-if="editedTimeTracking && editedTimeTracking.timeTrackingState === 'STARTED'">
          <Button class="btn btn-narrow btn-danger ml-2 mb-2" @click="stopTimeTracking">
            {{ $t('stop') }}
          </Button>
        </template>
        <Button
          v-else
          class="btn btn-narrow btn-inverse ml-2 mb-2"
          @click="onSaveEdit"
          :disabled="!isFormValid"
        >
          {{ $t('save') }}
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import { FilterMatchMode } from 'primevue/api';
import { getTimeTrackingStateColor } from '@/helpers/colors';
import ButtonWait from '@/components/ButtonWait.vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

import { yesNo } from '@/helpers/enums';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import { LoopingRhombusesSpinner } from 'epic-spinners';
import ConfirmButton from '@/components/ConfirmButton';
import Multiselect from 'vue-multiselect';

export default {
  name: 'TimeTrackingTable',
  components: {
    DataTable,
    Column,
    Calendar,
    MultiSelect,
    ButtonWait,
    InputText,
    LoopingRhombusesSpinner,
    // ConfirmButton,
    Dialog,
    Button,
    // Dropdown,
    Multiselect,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      filteredUsers: [],
      displayEditDialog: false,
      editedTimeTracking: null,
      originalStartTime: null,
      isNewTimeTracking: false,
      filteredEmployees: [],

      defaultFilters: {
        projectNumber: { value: null, matchMode: FilterMatchMode.EQUALS },
        dateRange: {
          range: [
            this.$dayjs()
              .startOf('day')
              .toDate(),
            this.$dayjs()
              .endOf('day')
              .toDate(),
          ],
        },
      },
      defaultCustomFilters: {
        timeTrackingStates: [],
        timeTrackingCustomerSearch: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
        timeTrackingUserSearch: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      },

      tableState: {
        filterName: 'timeTrackings-table-filters',
        customFilterName: 'timeTrackings-custom-table-filters',
        filters: this.defaultFilters,
        customFilters: this.defaultCustomFilters,
        pagination: {
          page: 0,
          rowsPerPage: 50,
        },
        sortField: 'startTime',
        sortOrder: -1,
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters([
      'getTimeTrackings',
      'getEmployees',
      'getEnumValues',
      'getTimeTrackingCount',
      'getTimeTrackingTotalHours',
      'isLoading',
      'getUsers',
    ]),
    isFormValid() {
      if (!this.editedTimeTracking) return false;
      return (
        this.editedTimeTracking.user &&
        this.editedTimeTracking.startTime &&
        this.editedTimeTracking.stopTime
      );
    },
    getTimeTrackingTotalHours() {
      return this.timeTrackings.reduce((total, tracking) => {
        return total + tracking.duration;
      }, 0);
    },
    timeTrackings() {
      return this.getTimeTrackings;
    },
    isProjectContext() {
      return this.project;
    },
    timeTrackingStates() {
      if (!this.getEnumValues('TimeTrackingState')) {
        return [];
      }
      return this.getEnumValues('TimeTrackingState').map((timeTrackingState) => {
        return { value: timeTrackingState, label: this.$t(timeTrackingState) };
      });
    },
    pageOffset() {
      return this.tableState.pagination.page * this.tableState.pagination.rowsPerPage;
    },
    filterName() {
      return 'timeTrackings-table-filters';
    },
    customFilterName() {
      return 'timeTrackings-custom-table-filters';
    },
  },
  methods: {
    ...mapActions([
      'fetchEnumValues',
      'fetchUserAttachmentUrl',
      'fetchProjects',
      'fetchProjectsPaginated',
      'fetchProjectsPaginatedByClient',
      'fetchEmployees',
      'fetchTimeTracking',
      'updateTimeTracking',
      'createTimeTracking',
      'deleteTimeTracking',
      'fetchUsers',
    ]),
    ...mapActions('auth', ['buildUserTimeTrackingExport', 'refreshUser']),

    async startTimeTrackingExportBuild() {
      if (this.getCurrentUser) {
        const userId = this.getCurrentUser.id;
        const totalRecords = this.getTimeTrackingCount;
        const page = 0;
        const pageSize = totalRecords > 0 ? totalRecords : this.tableState.pagination.rowsPerPage;
        const sortField = this.tableState.sortField;
        const sortOrder = this.tableState.sortOrder;
        const filters = {
          ...this.tableState.filters,
          ...this.tableState.customFilters,
        };

        await this.buildUserTimeTrackingExport({
          userId,
          page,
          pageSize,
          sortField,
          sortOrder,
          filters,
        });
      }
    },

    async fetchExportBuildState() {
      await this.refreshUser();

      if (this.getCurrentUser.timeTrackingExportBuildState === 'FINISHED') {
        this.downloadTimeTrackingExport('timeTrackingExportAttachment');
      }
      return this.getCurrentUser.timeTrackingExportBuildState;
    },

    async downloadTimeTrackingExport(attachmentType) {
      try {
        const url = await this.fetchUserAttachmentUrl({
          userId: this.getCurrentUser.id,
          attachmentType: attachmentType,
        });
        window.open(url);
      } catch (error) {
        console.error('Error fetching attachment URL:', error);
      }
    },
    async loadTimeTrackingData() {
      try {
        await this.fetchTimeTracking({
          page: this.pageOffset,
          pageSize: this.tableState.pagination.rowsPerPage,
          sortField: this.tableState.sortField,
          sortOrder: this.tableState.sortOrder,
          filters: {
            ...this.tableState.filters,
            ...this.tableState.customFilters,
            timeTrackingStates: this.tableState.customFilters.timeTrackingStates,
          },
        });
      } catch (error) {
        console.error('Fehler beim Laden der Zeitverfolgungsdaten:', error);
      }
    },
    formatDateWithDots(datetime) {
      const dateObj = new Date(datetime);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}.${month}.${year}`;
    },

    setupFilters() {
      this.tableState.filters = this.tableState.filters
        ? this.tableState.filters
        : this.defaultFilters;
      this.tableState.customFilters = this.tableState.customFilters
        ? this.tableState.customFilters
        : this.defaultCustomFilters;

      this.tableState.pagination.page = 0;
      this.tableState.pagination.rowsPerPage = 50;
      this.tableState.sortField = this.tableState.sortField
        ? this.tableState.sortField
        : this.defaultSortField;

      this.tableState.sortOrder = this.tableState.sortOrder
        ? this.tableState.sortOrder
        : this.defaultSortOrder;
    },

    async onFilter(event) {
      const filters = {
        projectNumber: this.tableState.filters.projectNumber?.value || null,
        timeTrackingCustomerSearch:
          this.tableState.customFilters.timeTrackingCustomerSearch?.value || null,
        timeTrackingUserSearch: this.tableState.customFilters.timeTrackingUserSearch?.value || null,
      };

      // force dateRange end date to end of day
      if (this.tableState.filters.dateRange.range && this.tableState.filters.dateRange.range[1]) {
        // Set end date to the end of the day
        this.tableState.filters.dateRange.range[1] = this.$dayjs(
          this.tableState.filters.dateRange.range[1]
        )
          .endOf('day')
          .toDate();
      }

      filters.dateRange = this.tableState.filters.dateRange;

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null) {
          delete filters[key];
        }
      });

      await this.loadTimeTrackingData(filters);
    },
    /**
     * Funktion zum Filtern des Zeitverfolgungsstatus.
     * @param {Array} value - Die ausgewählten Statuswerte, nach denen gefiltert werden soll.
     */
    async onTimeTrackingStateFilter(value) {
      this.tableState.customFilters.timeTrackingStates = this.timeTrackingStates.filter(
        (timeTrackingState) => value.includes(timeTrackingState)
      );

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadTimeTrackingData();
    },
    async onCustomerFilter(value) {
      this.tableState.customFilters.timeTrackingCustomerSearch.value = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadTimeTrackingData();
    },

    async onUserFilter(value) {
      this.tableState.customFilters.timeTrackingUserSearch.value = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadTimeTrackingData();
    },

    async onStateRestore(event) {
      const customFiltersFromStorage = JSON.parse(
        localStorage.getItem(this.tableState.customFilterName)
      );
      this.tableState.customFilters = customFiltersFromStorage
        ? customFiltersFromStorage
        : this.defaultCustomFilters;

      this.setupFilters();
    },

    rowClass(data) {
      return data.number === '_new' ? 'new' : null;
    },

    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
    },

    /**
     * send task data to server after editing a cell
     * @param {*} event
     */
    async onCellEditComplete(event) {
      let { data, newValue, field } = event;

      data[field] = newValue;

      this.save(data);
    },
    async onSelectState(row) {
      const { data } = row;
      this.save(data);
    },
    async onPage(event) {
      this.tableState.pagination.page = event.page;
      this.tableState.pagination.rowsPerPage = event.rows;
      await this.loadTimeTrackingData();
    },

    async onSort(event) {
      this.tableState.sortField = event.sortField;
      this.tableState.sortOrder = event.sortOrder;
      await this.loadTimeTrackingData();
    },

    onCalendarChange(e, data) {
      this.preventHideDialog = true;
      console.log('onCalendarChange():', e, data);
    },

    resetPreventHide() {
      this.$nextTick(() => {
        this.preventHideDialog = false;
      });
    },

    onDeleteTimeTracking(timeTrackingNumber) {
      // console.log('onDeleteTimeTracking():', timeTrackingNumber);
      this.deleteTimeTracking(timeTrackingNumber);
    },

    onEditTimeTracking(timeTrackingNumber) {
      console.log('onEditTimeTracking():', timeTrackingNumber);
      // this.editTimeTracking(timeTrackingNumber);
    },

    getTimeTrackingStateColor,

    showNewTimeTrackingDialog() {
      const now = new Date();
      const startTime = this.initStartTime(now);
      const stopTime = this.initStopTime(startTime);

      this.editedTimeTracking = {
        number: '_new',
        startTime: startTime,
        stopTime: stopTime,
        timeTrackingState: 'STOPPED',
        user: null,
      };
      this.isNewTimeTracking = true;
      this.displayEditDialog = true;
    },

    initStartTime(date) {
      return this.$dayjs(date)
        .hour(8)
        .minute(0)
        .second(0)
        .toDate();
    },

    initStopTime(date) {
      return this.$dayjs(this.initStartTime(date))
        .add(8, 'hours')
        .toDate();
    },

    showEditDialog(timeTracking) {
      // Create a copy of the timeTracking object to avoid direct mutation
      this.editedTimeTracking = {
        ...timeTracking,
        startTime: new Date(timeTracking.startTime),
        stopTime:
          timeTracking.timeTrackingState === 'STARTED'
            ? null
            : timeTracking.stopTime
            ? new Date(timeTracking.stopTime)
            : this.$dayjs(timeTracking.startTime)
                .add(8, 'hours')
                .toDate(),
      };
      // Cache the original time
      this.originalStartTime = this.$dayjs(timeTracking.startTime);
      this.isNewTimeTracking = false;
      this.displayEditDialog = true;
    },

    onCancelEdit() {
      this.displayEditDialog = false;
      this.editedTimeTracking = null;
      this.originalStartTime = null;
      this.isNewTimeTracking = false;
    },

    async onSaveEdit() {
      // console.log('onSaveEdit():', this.editedTimeTracking);
      if (this.editedTimeTracking) {
        if (this.isNewTimeTracking && !this.editedTimeTracking.user) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('error'),
            detail: this.$t('please_select_employee'),
            life: 3000,
          });
          return;
        }
        await this.save(this.editedTimeTracking);
        await this.loadTimeTrackingData();
      }
      this.displayEditDialog = false;
      this.editedTimeTracking = null;
      this.isNewTimeTracking = false;
    },

    async save(timeTracking) {
      if (this.isProjectContext) {
        timeTracking.project = this.project;
      }

      console.log('save():', timeTracking);

      if (timeTracking.number === '_new') {
        await this.createTimeTracking(timeTracking);
      } else {
        await this.updateTimeTracking({
          timeTracking,
        });
      }
    },

    async stopTimeTracking() {
      if (this.editedTimeTracking) {
        this.editedTimeTracking.stopTime = new Date();
        this.editedTimeTracking.timeTrackingState = 'STOPPED';
        await this.save(this.editedTimeTracking);
        this.displayEditDialog = false;
        this.editedTimeTracking = null;
      }
    },

    getDurationInMinutes(startTime, stopTime) {
      return this.$dayjs(stopTime).diff(this.$dayjs(startTime), 'minute');
    },

    onStartDateSelect(date) {
      console.log('onStartDateSelect():', date);
      this.editedTimeTracking.startTime = this.initStartTime(date);
    },

    onStopDateSelect(date) {
      this.editedTimeTracking.stopTime = this.initStopTime(date);
    },

    onStartTimeSelect(time) {
      // console.log('onStartTimeSelect():', time);
    },

    onStopTimeSelect(time) {
      // console.log('onStopTimeSelect():', time);
    },

    /**
     * Remove all filters
     * @param {*} event
     */
    async onRemoveFilters(event) {
      this.tableState.sortOrder = this.defaultSortOrder;
      this.tableState.sortField = this.defaultSortField;
      this.tableState.filters = this.defaultFilters;
      this.tableState.customFilters = this.defaultCustomFilters;
      this.tableState.pagination.page = 0;
      this.tableState.pagination.rowsPerPage = 50;
      this.tableState.sortField = 'startTime';
      this.tableState.sortOrder = -1;
      localStorage.removeItem(this.filterName);
      localStorage.removeItem(this.customFilterName);
      await this.loadTimeTrackingData();
    },

    onFilterUsers(query) {
      this.filteredUsers = [];
      this.getUsers.map((user) => {
        if (
          (user.firstname && user.firstname.toLowerCase().indexOf(query) >= 0) ||
          (user.lastname && user.lastname.toLowerCase().indexOf(query) >= 0)
        ) {
          this.filteredUsers.push(user);
        }
      });
    },
  },

  async created() {
    this.setupFilters();
  },

  async mounted() {
    await this.fetchEnumValues('TimeTrackingState');
    await this.fetchUsers();
    this.filteredEmployees = this.getEmployees;
    this.filteredUsers = this.getUsers;

    await this.loadTimeTrackingData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-menu-button {
  display: none;
}
::v-deep .p-column-filter-clear-button {
  display: none;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.state-badge {
  margin-left: 1rem;
}

.user-info {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.date-info {
  color: #666;
  font-size: 0.95rem;
}

.time-inputs {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.user-input {
  width: 100%;
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .multiselect-users {
    width: 100%;
  }
}

.time-row {
  display: flex;
  gap: 1rem;
  width: 100%;
}

.time-input {
  flex: 1;
}

.time-input label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.time-input-with-calendar {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .p-button {
    padding: 0.5rem;
    min-width: 2.5rem;
    height: 2.5rem;
  }
}

.multiselect-users {
  width: 100%;
}

::v-deep .p-datepicker-title {
  display: flex;
}

::v-deep .p-calendar .p-inputtext {
  width: 100%;
}

::v-deep .p-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.date-calendar {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}
</style>
